import { LitElement, html, css, unsafeCSS } from 'lit-element';
import { STYLES } from '../../autogen-css/styles';
import './dropdown-pagination';
import './error-pets';
import './last-pet-card';
import './loading-pets';
import './multi-select';
import './no-available-pets';
import './no-matching-pets';
import './pet-card';
import _debounce from 'lodash/debounce';
import { ANIMAL_TYPES_ENUM } from '../../core/scripts/types/animal-types';
import { focusFirstFocusable } from '../../core/scripts/lib/focusManager/index';
import Client from './Client'

// Build information (injected by webpack)
const BUILD_INFO = typeof __BUILD_INFO__ !== 'undefined' ? __BUILD_INFO__ : {
    buildDate: 'unknown',
    version: 'unknown'
};

/**
 * The list of known filters
 * @type {Array}
 */
const FILTERS = ['type', 'breed', 'size', 'gender', 'age', 'goodWith', 'care'];

/**
 * The list of simulated breakpoints compared against the window width and the
 * corresponding class to assign to the filters container
 * @type {Array}
 */
const BREAKPOINTS = [
    {
        width: 580,
        name: 'Md',
    },
    {
        width: 860,
        name: 'Lg',
        mobileMaximumWidth: true,
    },
    {
        width: 1100,
        name: 'Xl',
    },
];

/**
 * The class applied to the filters container to display the rest of the filters
 * @type {String}
 */
const ALL_FILTERS_SHOWING_CLASS = 's-showMore';

/**
 * The class applied to the results container to indicate results are re-rendering
 * @type {String}
 */
const LOADING_CLASS = 's-loading';

/**
 * Container component for the embeddable pet list
 * @class PetScroller
 * @extends LitElement
 */
class PetScroller extends LitElement {
    static styles = css`
        ${unsafeCSS(STYLES)}
    `;

    static get properties() {
        return {
            // Public
            // --------
            apiBase: { type: String },
            petfinderUrl: { type: String },
            petStatus: { type: String },
            containerWidth: { type: Number },
            pageSize: { type: Number },
            showBreedName: { type: Boolean },
            s3Url: { type: String },
            petlistUrl: { type: String },

            // Optional locked values from config page
            type: { type: Array },
            breed: { type: Array },
            size: { type: Array },
            gender: { type: Array },
            age: { type: Array },
            goodWith: { type: Array },
            care: { type: Array },
            limit: { type: Number },
            hideBreed: { type: String },
            petListTitle: { type: String },

            // Extra hidden options
            organization: { type: Array },
            status: { type: String },

            // Internal
            // --------
            _client: { type: Object },
            _animals: { type: Array },
            _currentPage: { type: Number },
            _totalPages: { type: Number },
            _allFiltersShowing: { type: Boolean },
            _loading: { type: Boolean },
            _error: { type: Boolean },
            _firstPetCardHeight: { type: Number },

            // A11y: status message announced to screen readers when results change
            _resultsStatusMessage: { type: String },

            // Filter options
            _typeItems: { type: Array },
            _breedItems: { type: Array },
            _sizeItems: { type: Array },
            _genderItems: { type: Array },
            _ageItems: { type: Array },
            _goodWithItems: { type: Array },
            _careItems: { type: Array },

            // User applied filters
            _typeApplied: { type: Array },
            _breedApplied: { type: Array },
            _sizeApplied: { type: Array },
            _genderApplied: { type: Array },
            _ageApplied: { type: Array },
            _goodWithApplied: { type: Array },
            _careApplied: { type: Array },
        };
    }

    constructor() {
        super();

        this.apiBase = '';
        this.petfinderUrl = '';
        this.containerWidth = 800; // Set a reasonable default width (between mobile and desktop)

        this.type = [];
        this.breed = [];
        this.size = [];
        this.gender = [];
        this.age = [];
        this.goodWith = [];
        this.care = [];
        this.limit = 24;
        this.hideBreed = 'false';
        this.petListTitle = '';

        this.organization = [];
        this.status = '';

        this._client = {};
        this._rootElement = {};
        this._animals = [];
        this._currentPage = 1;
        this._totalPages = 1;
        this._allFiltersShowing = false;
        this._loading = true;
        this._error = false;
        this._firstPetCardHeight = 0;

    // A11y: live region message for result count updates
    this._resultsStatusMessage = '';

        this._typeItems = [];
        this._breedItems = [];
        this._sizeItems = [];
        this._genderItems = [];
        this._ageItems = [];
        this._goodWithItems = [];
        this._careItems = [];

        this._typeApplied = [];
        this._breedApplied = [];
        this._sizeApplied = [];
        this._genderApplied = [];
        this._ageApplied = [];
        this._goodWithApplied = [];
        this._careApplied = [];
        this.s3Url = '';

        this._lastPage = false;

        // cached reference to parent container
        this._containerElement = this;

        // Resize this component when the window resizes
        this._resizeObserver = null;
        this._windowResizeHandler = null;

        // Initial width will be set in connectedCallback when element is in DOM
    }

    /**
     * Get build information for debugging
     * @returns {Object} Build information object
     */
    static getBuildInfo() {
        return BUILD_INFO;
    }

    updateContainerWidth(retryCount = 0) {
        // Safety check: ensure element is properly initialized
        if (!this || !this.isConnected) {
            return;
        }
        
        const rect = this.getBoundingClientRect();
        let width = rect.width;
        
        // If width is 0, try to get width from parent element
        if (width === 0) {
            const parent = this.parentElement;
            if (parent) {
                const parentRect = parent.getBoundingClientRect();
                width = parentRect.width;
                
                // If parent also has no width, try walking up the DOM tree
                if (width === 0) {
                    let ancestor = parent.parentElement;
                    let ancestorLevel = 1;
                    while (ancestor && width === 0) {
                        const ancestorRect = ancestor.getBoundingClientRect();
                        width = ancestorRect.width;
                        ancestor = ancestor.parentElement;
                        ancestorLevel++;
                        
                        // Don't go past body element
                        if (ancestor === document.body) {
                            break;
                        }
                    }
                }
            }
        }
        
        // Only use window width as absolute last resort and add some padding
        if (width === 0) {
            const fallbackWidth = Math.min(window.innerWidth * 0.9, 1200);
            width = fallbackWidth;
        }
        
        // If still 0, the element might not be rendered yet, try again with retry limit
        if (width === 0 && retryCount < 5 && this.isConnected) {
            setTimeout(() => this.updateContainerWidth(retryCount + 1), 100);
            return;
        }
        
        const oldWidth = this.containerWidth;
        this.containerWidth = width;
        
        // Safety check before setAttribute
        if (this && typeof this.setAttribute === 'function') {
            this.setAttribute('containerwidth', width);
        }
        
        if (oldWidth !== width) {
            this.requestUpdate(); // Force a re-render when width changes
        }
    }

    windowResizeHandler() {
        this.updateContainerWidth();
    }

    /**
     * Returns the hideBreed value as a proper boolean, handling string attributes
     * @returns {Boolean}
     */
    get hideBreedValue() {
        // Handle string attributes: "true", "false", or boolean values
        if (typeof this.hideBreed === 'string') {
            return this.hideBreed.toLowerCase() === 'true';
        }
        return Boolean(this.hideBreed);
    }

    /**
     * Returns the currently selected or preconfigured Animal Type
     * @returns {String}
     */
    get currentAnimalType() {
        const selectedType = this.type.length > 0 ? this.type[0] : this._typeApplied[0];
        
        // If the selectedType is an internal key (like 'scales-fins-other'), 
        // convert it to the display label (like 'Scales, Fins, & Other')
        if (selectedType && ANIMAL_TYPES_ENUM[selectedType]) {
            return ANIMAL_TYPES_ENUM[selectedType];
        }
        
        // Otherwise return as-is (it's already a display label or undefined)
        return selectedType;
    }

    /**
     * Returns search parameters for use with the public API.
     * Takes either the locked value or the user selected one.
     * @returns {Object}
     */
    // TODO: We should refactor this so we don't have to strip values after
    get formattedFilters() {
        const formattedFilters = {
            type: this.currentAnimalType,
            breed:
                this.breed.length > 0
                    ? this.breed.join(',')
                    : this._breedApplied,
            size:
                this.size.length > 0
                    ? this.size.join(',')
                    : this._sizeApplied,
            gender:
                this.gender.length > 0
                    ? this.gender.join(',')
                    : this._genderApplied,
            age:
                this.age.length > 0
                    ? this.age.join(',')
                    : this._ageApplied,
            good_with_children:
                this.goodWith.length > 0
                    ? this.goodWith.includes('children')
                    : this._goodWithApplied.includes('children'),
            good_with_dogs:
                this.goodWith.length > 0
                    ? this.goodWith.includes('dogs')
                    : this._goodWithApplied.includes('dogs'),
            good_with_cats:
                this.goodWith.length > 0
                    ? this.goodWith.includes('cats')
                    : this._goodWithApplied.includes('cats'),
            declawed:
                this.care.length > 0
                    ? this.care.includes('declawed')
                    : this._careApplied.includes('declawed'),
            house_trained:
                this.care.length > 0
                    ? this.care.includes('house_trained')
                    : this._careApplied.includes('house_trained'),
            special_needs:
                this.care.length > 0
                    ? this.care.includes('special_needs')
                    : this._careApplied.includes('special_needs'),
            organization: this.organization,
            status: this.status,
            page: this._currentPage,
            limit: this.limit,
        };

        // Strip out falsy values
        // TODO: Refactor away from this
        Object.keys(formattedFilters).forEach(key => {
            const value = formattedFilters[key];
            if (value) {
                return;
            }
            delete formattedFilters[key];
        });

        return formattedFilters;
    }

    /**
     * Returns a loading class if the user paginates or re-filters the results
     * @returns {String}
     */
    get loadingClass() {
        return this._loading && this._animals.length > 0 ? LOADING_CLASS : '';
    }

    /**
     * Holds the logic for whether each filter should be hidden
     * @param  {String} filter
     * @returns {Boolean}
     */
    isFilterHidden(filter) {
        switch (filter) {
            case 'type':
                return this.type.length > 0;
            case 'breed':
                return (
                    this.breed.length > 0 ||
                    (this.type.length === 0 && this._typeApplied.length === 0)
                );
            case 'size':
                return this.size.length > 0;
            case 'gender':
                return this.gender.length > 0;
            case 'age':
                return this.age.length > 0;
            case 'goodWith':
                return this.goodWith.length > 0;
            case 'care':
                return this.care.length > 0;
            default:
                return false;
        }
    }

    /**
     * Returns the number of currently visible filters
     * @returns {Number}
     */
    get visibleFilterCount() {
        return FILTERS.filter(filter => {
            return !this.isFilterHidden(filter);
        }).length;
    }

    /**
     * Handles any selections in the filter subcomponents
     * @param  {Object} event
     */
    filterChangeHandler(event) {
        this[this.getAppliedFilterProperty(event.detail.identifier)] =
            event.detail.values;

        // Reset the pagination
        this._currentPage = 1;

        if (event.detail.identifier === 'type') {
            // Reset any filters that depend on Animal Type
            this._breedApplied = [];
            this._careApplied = [];

            if (event.detail.values.length > 0) {
                // Refresh the Breeds dropdown when a new Animal Type is chosen
                this.getAnimalBreeds(event.detail.values[0]);
            }
        }

        this.getAnimals();
    }

    /**
     * Handles the removal of a single filter
     * @param  {string} filter
     * @param  {string} value
     */
    removeFilter(filter, value) {
        this[this.getAppliedFilterProperty(filter)] = this[
            this.getAppliedFilterProperty(filter)
        ].filter(v => v !== value);

        this.getAnimals();
    }

    /**
     * Handles clicking Clear All
     * @param  {Object} event
     */
    clearAllClickHandler(event) {
        FILTERS.forEach(filter => {
            this[this.getAppliedFilterProperty(filter)] = [];
        });

        this.getAnimals();
    }

    /**
     * Handles interactions with pagination
     * @param  {Object} event
     */
    pageChangeHandler(event) {
        this._currentPage = event.detail.page;

        // call focusFirstFocusable() after pet cards have rendered
        this.getAnimals(true);
    }

    /**
     * Handles the first pet card having finished rendering. This is used
     * to get an appropriate pet card height on page load to set the
     * last pet card height.
     * @param  {Object} event
     */
    petCardRenderHandler(event) {
        this._firstPetCardHeight = event.detail.height;
    }

    /**
     * Retrieves a filtered list of animals
     */
    getAnimals(focusFirst = false) {
        this._error = false;
        this._loading = true;

        const response = this._client.fetchAnimals(this.formattedFilters, this._currentPage).then((res) => {
            if (res.animals) {
                this._animals = res.animals;
                this._totalPages = Math.min(Math.ceil(res.totalCount / this.limit), 99)
                this._lastPage = this._currentPage === Math.min(Math.ceil(res.totalCount / this.limit), 99)

                // A11y: announce updated result counts after filtering.
                // Only announce when not loading and when this fetch corresponds to current state.
                const totalCount = Number.isFinite(res.totalCount)
                    ? res.totalCount
                    : this._animals.length;
                if (totalCount > 0) {
                    this._resultsStatusMessage = `${totalCount} matching pets found`;
                } else if (this.appliedFilters.length > 0) {
                    this._resultsStatusMessage = 'No matching pets found';
                } else {
                    // When no filters are applied, avoid being too chatty.
                    this._resultsStatusMessage = '';
                }

                if (focusFirst) {
                    // reset focus / scroll to the top on pagination change
                    focusFirstFocusable(this._rootElement);
                }
            }

        })
            .catch(error => {
                this._error = true;
                // A11y: announce errors in a polite way (existing UI shows an error component).
                this._resultsStatusMessage = 'An error occurred while loading pets';
            })
            .finally(() => {
                this._loading = false;
            });
    }

    /**
     * Retrieves all breed information for a given type
     * @param  {string} type
     */
    getAnimalBreeds(type) {
        this._client.animalData(type)
            .then(response => {
               const { speciesList } = response
                const primaryBreeds = speciesList?.[0]?.primaryBreeds || []
                if (primaryBreeds.length === 0) {
                    throw new Error('No breeds found');
                }

                this._breedItems = primaryBreeds.map(breed => ({
                    label: breed.name,
                    value: breed.name.toLowerCase(),
                }));
                this.setDefaults(this._breedItems, this.breed);
            })
            .catch(error => {
                // Network error handling should be covered by getAnimals()
            });
    }

    /**
     * Populates the other filters that do not require dynamic data
     */
    getStaticFilters() {
        // In animal-types.js, "Any" is an enumerated value, but we don't need
        // to send a value for the "type" search parameter if we want to return
        // animals of any type. Thus we take it out of the selectable values,
        // and make sure Animal Type is an optional filter.
        this._typeItems = Object.keys(ANIMAL_TYPES_ENUM)
            .filter(key => key !== 'any')
            .map(key => ({
                label: ANIMAL_TYPES_ENUM[key],
                value: key,
            }));

        this.setDefaults(this._typeItems, this.type);

        this._ageItems = [
            { label: 'Baby', value: 'baby' },
            { label: 'Young', value: 'young' },
            { label: 'Adult', value: 'adult' },
            { label: 'Senior', value: 'senior' },
        ];
        this.setDefaults(this._ageItems, this.age);

        this._genderItems = [
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' },
        ];
        this.setDefaults(this._genderItems, this.gender);

        this._sizeItems = [
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
            { label: 'XL', value: 'xlarge' },
        ];
        this.setDefaults(this._sizeItems, this.size);

        this._goodWithItems = [
            { label: 'Kids', value: 'children' },
            { label: 'Dogs', value: 'dogs' },
            { label: 'Cats', value: 'cats' },
        ];
        this.setDefaults(this._goodWithItems, this.goodWith);

        this._careItems = [
            { label: 'Declawed', value: 'declawed', animal_type: 'cat' },
            { label: 'House-trained', value: 'house_trained' },
            { label: 'Special Needs', value: 'special_needs' },
        ];
        this.setDefaults(this._careItems, this.care);
    }

    /**
     * Used to apply the public property value to the specified filter
     * @param  {Array} items
     * @param  {Array} selected
     */
    setDefaults(items, selected) {
        items.forEach(item => {
            item.selected = selected.includes(item.value);
        });
    }

    /**
     * Returns the name of the property storing the current selection(s) for a given filter
     * @param  {string} name
     */
    getAppliedFilterProperty(name) {
        return '_' + name + 'Applied';
    }

    /**
     * Returns all filter options that have been selected
     * @returns {Array}
     */
    get appliedFilters() {
        let filters = [];

        FILTERS.forEach(name => {
            const items = this['_' + name + 'Items'];
            this[this.getAppliedFilterProperty(name)].forEach(value => {
                const appliedItem = items.find(item => item.value === value);
                const label = appliedItem ? appliedItem.label : '';
                filters.push({ name: name, value: value, label: label });
            });
        });

        return filters;
    }

    /**
     * Returns all breakpoint classes less than the current window width
     * @returns {String}
     */
    get containerQueryClasses() {
        const classes = [];
        const width = this.containerWidth;
        
        // Determine the current breakpoint range and apply appropriate classes
        if (width >= 1100) {
            // XL: 4 columns
            classes.push('cq-minMd', 'cq-minLg', 'cq-minXl');
        } else if (width >= 860) {
            // LG: 3 columns  
            classes.push('cq-minMd', 'cq-minLg', 'cq-maxXl');
        } else if (width >= 580) {
            // MD: 2 columns
            classes.push('cq-minMd', 'cq-maxLg', 'cq-maxXl');
        } else {
            // SM: 1 column
            classes.push('cq-maxMd', 'cq-maxLg', 'cq-maxXl');
        }
        
        const classString = classes.join(' ');
        
        return classString;
    }

    /**
     * Returns whether the component's width is small enough to be considered "mobile"
     * vs. "desktop".
     * @returns {Boolean}
     */
    get isMobileView() {
        const desktopWidth =
            BREAKPOINTS.find(breakpoint => {
                return breakpoint.mobileMaximumWidth;
            }).width || 0;

        return this.containerWidth < desktopWidth;
    }

    /**
     * Returns the "all filters showing" class if the button has been clicked
     * @returns {String}
     */
    get filterToggleClass() {
        return this._allFiltersShowing ? ALL_FILTERS_SHOWING_CLASS : '';
    }

    /**
     * Returns either: no available pets, no matching pets (based on the user's filters),
     * or the list of matching pets.
     * @returns {TemplateResult}
     */
    get resultsView() {
        // NOTE: Starting to need a state machine. ^_^

        if (this._error) {
            return html`
                <error-pets></error-pets>
            `;
        } else if (this._animals.length === 0 && this._loading) {
            return html`
                <loading-pets></loading-pets>
            `;
        } else if (this._animals.length === 0) {
            if (this.appliedFilters.length === 0) {
                return html`
                    <no-available-pets></no-available-pets>
                `;
            } else {
                return html`
                    <no-matching-pets
                        @reset="${this.clearAllClickHandler}"
                    ></no-matching-pets>
                `;
            }
        } else {
            return html`
                ${this.petListTitle !== ''
                    ? html`
                          <h2 class="embeddablePetList-title txt txt_h2 u-vr6x">
                              ${this.petListTitle}
                          </h2>
                      `
                    : ''}
                <div class="grid grid_gutter m-grid_stretch u-vr6x">
                    ${this._animals.map(
                        (animal, index) =>
                            html`
                                <div class="grid-col grid-col_result">
                                    <!-- we only need one pet card to fire the render handler, thus index === 0 -->
                                    <pet-card
                                        .animal=${animal}
                                        .hideBreed=${this.hideBreedValue}
                                        .reportRender=${index === 0}
                                        .s3Url=${this.s3Url}
                                        .petfinderUrl=${this.petfinderUrl}
                                        @render="${this.petCardRenderHandler}"
                                    >
                                    </pet-card>
                                </div>
                            `
                    )}
                    ${this._lastPage
                        ? html`
                              <div class="grid-col grid-col_result">
                                  <last-pet-card
                                      cardheight="${this._firstPetCardHeight}"
                                      .petfinderUrl=${this.petfinderUrl}
                                  ></last-pet-card>
                              </div>
                          `
                        : ''}
                </div>
                <dropdown-pagination
                    .currentPage="${this._currentPage}"
                    .totalPages="${this._totalPages}"
                    @change="${this.pageChangeHandler}"
                ></dropdown-pagination>
            `;
        }
    }

    /**
     * Returns a list of chicklets representing user-applied filters
     * @returns {TemplateResult}
     */
    get appliedFiltersView() {
        if (!this.appliedFilters.length > 0) {
            return;
        }

        return html`
            <div class="embeddablePetList-appliedFilters">
                <div class="txt m-txt_bold u-vr2x">
                    Filters Applied
                </div>
                <ul class="wrapList wrapList_gutterSm">
                    ${this.appliedFilters.map((filter, index) => {
                        const isLastFilter =
                            this.appliedFilters.length === index + 1;
                        return html`
                            <li>
                                <div
                                    class="filterTag${isLastFilter
                                        ? ' m-filterTag_last'
                                        : ''}"
                                >
                                    <span class="filterTag-title"
                                        >${filter.label}</span
                                    >
                                    <button
                                        tabindex="0"
                                        class="filterTag-removeBtn"
                                        aria-label="${`Remove filter ${filter.label}: ${filter.value}`}"
                                        @click="${event => {
                                            this.removeFilter(
                                                filter.name,
                                                filter.value
                                            );
                                        }}"
                                    >
                                        Remove Filter
                                        <span
                                            class="icon icon_xs m-icon_colorWhite"
                                        >
                                            <svg
                                                role="presentation"
                                                focusable="false"
                                                viewBox="0 0 24.67 24.67"
                                            >
                                                <title>icon-close</title>
                                                <path
                                                    d="M17.27,12.34l6.4-6.4A3.49,3.49,0,0,0,18.74,1l-6.4,6.4L5.93,1A3.49,3.49,0,0,0,1,5.93l6.4,6.4L1,18.74a3.49,3.49,0,0,0,4.94,4.94l6.4-6.4,6.4,6.4a3.49,3.49,0,0,0,4.94-4.94Z"
                                                ></path>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                                ${isLastFilter
                                    ? html`
                                          <button
                                              tabindex="0"
                                              class="txt txt_link m-txt_heavy m-txt_alignMiddle u-displayInlineBlock"
                                              @click="${this
                                                  .clearAllClickHandler}"
                                          >
                                              Clear All
                                          </button>
                                      `
                                    : ''}
                            </li>
                        `;
                    })}
                </ul>
            </div>
        `;
    }

    /**
     * Returns the button that toggles hidden filters
     * @returns {TemplateResult}
     */
    get toggleButtonView() {
        let buttonText = 'LESS';

        if (!this._allFiltersShowing) {
            buttonText = this.isMobileView ? 'FILTER' : 'MORE';
        }

        return html`
            <div class="embeddablePetList-controls-toggle">
                <button
                    class="fieldBtn m-fieldBtn_full m-fieldBtn_tight"
                    type="button"
                    aria-expanded="${this._allFiltersShowing
                        ? 'true'
                        : 'false'}"
                    aria-controls="filters_container"
                    @click="${this.toggleFiltersClickHandler}"
                >
                    <span class="fieldBtn-label">
                        ${buttonText}
                    </span>
                    <span class="fieldBtn-icon">
                        <span class="icon m-icon_colorPrimary">
                            <svg
                                id="icon-filter"
                                viewBox="0 0 31.9 27.4"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    d="M1.92,5.72H18.1a3.72,3.72,0,0,0,6.38,0H30a1.9,1.9,0,1,0,0-3.81H24.54a3.71,3.71,0,0,0-6.49,0H1.92a1.9,1.9,0,1,0,0,3.81Z"
                                ></path>
                                <path
                                    d="M30,11.75H13.77a3.72,3.72,0,0,0-6.33,0H1.92a1.9,1.9,0,1,0,0,3.81H7.34a3.71,3.71,0,0,0,6.54,0H30a1.9,1.9,0,1,0,0-3.81Z"
                                ></path>
                                <path
                                    d="M30,21.74H24.53a3.71,3.71,0,0,0-6.44,0H1.92a1.9,1.9,0,1,0,0,3.81H18.09a3.71,3.71,0,0,0,6.43,0H30a1.9,1.9,0,1,0,0-3.81Z"
                                ></path>
                            </svg>
                        </span>
                    </span>
                </button>
            </div>
        `;
    }

    toggleFiltersClickHandler() {
        const wasShowing = this._allFiltersShowing;
        this._allFiltersShowing = !wasShowing;

        if (!wasShowing) {
            this.focusExpandedFilters();
        }
    }

    focusExpandedFilters() {
        this.updateComplete.then(() => {
            setTimeout(() => {
                const secondaryFilterControl = this.shadowRoot.querySelector(
                    '#filters_container .embeddablePetList-controls-filters_secondary input, #filters_container .embeddablePetList-controls-filters_secondary .multiselect-field'
                );
                const firstFilterControl = this.shadowRoot.querySelector(
                    '#filters_container .embeddablePetList-controls-filters input, #filters_container .embeddablePetList-controls-filters .multiselect-field'
                );
                const focusTarget = secondaryFilterControl || firstFilterControl;

                if (focusTarget) {
                    focusTarget.focus();
                }
            }, 0);
        });
    }

    get _visibleFilterIds() {
        return FILTERS.filter(filter => !this.isFilterHidden(filter));
    }

    get _desktopFirstRowFilterCount() {
        if (this.containerWidth >= 1100) {
            return 4;
        }

        if (this.containerWidth >= 860) {
            return 3;
        }

        return this._visibleFilterIds.length;
    }

    get _renderedFilterGroups() {
        const visibleFilters = this._visibleFilterIds;

        if (this.isMobileView) {
            return this._allFiltersShowing ? [visibleFilters] : [];
        }

        const firstRowCount = this._desktopFirstRowFilterCount;

        if (!this._allFiltersShowing) {
            return [visibleFilters.slice(0, firstRowCount)];
        }

        return [
            visibleFilters.slice(0, firstRowCount),
            visibleFilters.slice(firstRowCount),
        ].filter(group => group.length > 0);
    }

    renderFilterById(filter) {
        switch (filter) {
            case 'type':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="type"
                            label="Animal Type"
                            .multiple=${false}
                            .items="${this._typeItems}"
                            .selected="${this._typeApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'breed':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="breed"
                            label="Breed"
                            .autosuggest="${true}"
                            .items="${this._breedItems}"
                            .selected="${this._breedApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'size':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="size"
                            label="Size"
                            .items="${this._sizeItems}"
                            .selected="${this._sizeApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'age':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="age"
                            label="Age"
                            .items="${this._ageItems}"
                            .selected="${this._ageApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'gender':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="gender"
                            label="Gender"
                            .items="${this._genderItems}"
                            .selected="${this._genderApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'goodWith':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="goodWith"
                            label="Good With"
                            .items="${this._goodWithItems}"
                            .selected="${this._goodWithApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            case 'care':
                return html`
                    <div class="grid-col grid-col_filter">
                        <multi-select
                            identifier="care"
                            label="Care & Behavior"
                            .items="${this._careItems.filter(item => {
                                return (
                                    item.animal_type === undefined ||
                                    item.animal_type == this.currentAnimalType
                                );
                            })}"
                            .selected="${this._careApplied}"
                            @change="${this.filterChangeHandler}"
                        >
                        </multi-select>
                    </div>
                `;
            default:
                return '';
        }
    }

    /**
     * LitElement life cycle event handler for rendering the markup, using lit-html
     * @returns {TemplateResult}
     */
    render() {
        const filterGroups = this._renderedFilterGroups;
        const [firstFilterGroup = [], ...remainingFilterGroups] = filterGroups;

        return html`
            <div
                class="embeddablePetList ${this.containerQueryClasses} ${this
                    .filterToggleClass} ${this.loadingClass}"
            >
                <a
                    class="embeddablePetList-logo u-vr10x"
                    href="${this.petfinderUrl}"
                    target="_blank"
                >
                    <div class="embeddablePetList-logo-preLine">
                        Powered by
                    </div>
                    <div class="embeddablePetList-logo-svg">Petfinder</div>
                    <div class="embeddablePetList-logo-tagLine">
                        Adopt your new best friend
                    </div>
                </a>
                <div
                    id="filters_container"
                    class="embeddablePetList-controls"
                    role="group"
                    aria-label="Filters"
                >
                    ${firstFilterGroup.length > 0
                        ? html`
                            <div class="embeddablePetList-controls-filters">
                                <div class="grid grid_gutter">
                                    ${firstFilterGroup.map(filter =>
                                        this.renderFilterById(filter)
                                    )}
                                </div>
                            </div>
                        `
                        : ''}
                    ${this.toggleButtonView}
                    ${remainingFilterGroups.map(
                        group => html`
                            <div class="embeddablePetList-controls-filters embeddablePetList-controls-filters_secondary">
                                <div class="grid grid_gutter">
                                    ${group.map(filter =>
                                        this.renderFilterById(filter)
                                    )}
                                </div>
                            </div>
                        `
                    )}
                </div>
                ${this.appliedFiltersView}
                <div
                    class="u-isVisuallyHidden"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    ${this._resultsStatusMessage}
                </div>
                <div class="embeddablePetList-results">
                    ${this.resultsView}
                </div>
            </div>
        `;
    }

    /**
     * Life cycle method called when the element is connected to the DOM
     */
    connectedCallback() {
        super.connectedCallback();
        // Ensure we get the correct width once the element is in the DOM
        setTimeout(() => {
            this.updateContainerWidth();
        }, 0);
    }

    /**
     * Life cycle method that is called after the first render.
     * Sets up the JS SDK and runs data queries.
     */
    firstUpdated() {
        
        // cache the shadow dom's root element after it has rendered
        // so we can focus the first focusable on pagination changes
        this._rootElement = this.shadowRoot.querySelector('.embeddablePetList');

        this._client = new Client(this.apiBase);

        // Set up resize observer for container width changes
        if (window.ResizeObserver) {
            this._resizeObserver = new ResizeObserver((entries) => {
                // Instead of using the ResizeObserver entry (which can be unreliable),
                // trigger our updateContainerWidth method which uses getBoundingClientRect
                this.updateContainerWidth();
            });
            
            // Observe the parent element if available, otherwise observe this element
            const targetElement = this.parentElement || this;
            this._resizeObserver.observe(targetElement);
        } else {
            // Fallback for older browsers
            this._windowResizeHandler = _debounce(() => {
                this.updateContainerWidth();
            }, 100);
            window.addEventListener('resize', this._windowResizeHandler);
        }

        // Set initial container width
        this.updateContainerWidth();

        this.getStaticFilters();
        if (this.type.length > 0) {
            this.getAnimalBreeds(this.type[0]);
        }
        this.getAnimals();
    }

    /**
     * Life cycle method that is called after render and lists updated render properties
     * @param  {Object} changedProperties
     */
    updated(changedProperties) {
        
        changedProperties.forEach((oldValue, propName) => {
            // Whenever the containerWidth property has caused a re-render, get the new pet card height
            if (propName === 'containerWidth') {
                const firstPetCard = this.shadowRoot.querySelector('.petCard');

                // firstPetCard won't have been rendered the first time we hit this code,
                // which is covered by petCardRenderHandler()
                if (firstPetCard) {
                    const newHeight = firstPetCard.getBoundingClientRect().height;
                    this._firstPetCardHeight = newHeight;
                }
            }
        }, this);
    }

    disconnectedCallback() {
        // Clean up resize observer
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        // Clean up window resize listener
        if (this._windowResizeHandler) {
            window.removeEventListener('resize', this._windowResizeHandler);
        }
        super.disconnectedCallback && super.disconnectedCallback();
    }
}

customElements.define('pet-scroller', PetScroller);
